import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useDevelopmentFixtures } from "../../api/client";
import {
  Button,
  SearchField,
  StatePanel,
  StatusBadge,
} from "../../components/ui";
import {
  bookingDirectory,
  type BookingDirectory,
} from "../../data/booking-directory";
import { useErpData } from "../../data/DataProvider";
import { formatDate } from "../../data/format";
import type { ServiceType } from "../../data/types";
import { ServiceTag } from "./ServiceTag";
import { caseUrl, copyLabel, money } from "./shared";
import { useListScroll } from "./useListScroll";

const states = ["all", "pending", "confirmed", "completed", "cancelled"];

/** Bookings for one service or all of them; filters live in the URL so returning from a case keeps them. */
export function BookingList({ service }: { service?: ServiceType | undefined }) {
  const { snapshot } = useErpData();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const state = params.get("state") ?? "all";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [directory, setDirectory] = useState<BookingDirectory | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  useListScroll(useDevelopmentFixtures ? !!snapshot : !!directory && !loading);
  useEffect(() => {
    if (useDevelopmentFixtures) return;
    let active = true;
    setLoading(true);
    setError("");
    bookingDirectory({ service, page, search: query, status: state })
      .then((result) => {
        if (active) setDirectory(result);
      })
      .catch((caught) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "No pudimos consultar las reservas.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [service, page, query, state, revision]);
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };
  const term = query.trim().toLocaleLowerCase("es-MX");
  const rows = useDevelopmentFixtures
    ? (snapshot?.bookings ?? []).filter(
        (b) =>
          (!service || b.service === service) &&
          (state === "all" ||
            (state === "pending" &&
              ["holding", "pending_payment"].includes(b.status)) ||
            b.status === state) &&
          (!term ||
            `${b.customer} ${b.reference} ${b.orderNumber} ${b.phone}`
              .toLocaleLowerCase("es-MX")
              .includes(term)),
      )
    : (directory?.data ?? []);
  const returnTo = location.pathname + location.search;
  return (
    <section className="workspace-card">
      <header>
        <div>
          <h2>Reservas</h2>
          <p>Clientes que ya reservaron: pagos, saldo y preparación.</p>
        </div>
        <span>
          {useDevelopmentFixtures
            ? rows.length
            : (directory?.pagination.total ?? "—")}{" "}
          resultados
        </span>
      </header>
      <div className="workspace-filters">
        <SearchField
          value={query}
          onChange={(v) => update("q", v)}
          placeholder="Cliente, reserva o pedido..."
        />
        <label className="workspace-inline-select">
          Estado
          <select
            value={state}
            onChange={(e) => update("state", e.target.value)}
          >
            {states.map((s) => (
              <option value={s} key={s}>
                {s === "all" ? "Todas" : copyLabel(s)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? (
        <StatePanel
          state="error"
          description={error}
          onRetry={() => setRevision((v) => v + 1)}
        />
      ) : loading ? (
        <StatePanel state="loading" />
      ) : (
        <div className="workspace-booking-list">
          {rows.map((b) => (
            <Link
              className="workspace-booking-row"
              key={b.id}
              to={caseUrl("booking", b.id, returnTo)}
            >
              <div>
                <small>
                  {b.reference} · Pedido {b.orderNumber}
                </small>
                {!service && <ServiceTag service={b.service} />}
                <strong>{b.customer}</strong>
                <span>{b.experience}</span>
              </div>
              <div>
                <strong>{formatDate(b.date)}</strong>
                <small>
                  {b.time} · {b.guests} personas
                </small>
              </div>
              <div>
                <small>Reserva</small>
                <StatusBadge
                  tone={b.status === "confirmed" ? "success" : "neutral"}
                >
                  {copyLabel(b.status)}
                </StatusBadge>
              </div>
              <div>
                <small>Pago</small>
                <StatusBadge tone={b.paid ? "success" : "warning"}>
                  {"payment" in b && b.payment
                    ? copyLabel(b.payment.status)
                    : b.paid
                      ? "Pago recibido · revisar saldo"
                      : "Por confirmar"}
                </StatusBadge>
              </div>
              {"payment" in b && b.payment && (
                <div className="booking-money">
                  <small>
                    Total {money(b.payment.total, b.payment.currency)}
                  </small>
                  <strong>
                    Recibido {money(b.payment.received, b.payment.currency)}
                  </strong>
                  {["cancelled", "expired", "failed", "refunded"].includes(
                    b.status,
                  ) ? (
                    <span>Reserva inactiva · revisar pedido</span>
                  ) : (
                    <span className={b.payment.overdue ? "text-danger" : ""}>
                      Saldo {money(b.payment.balance, b.payment.currency)}{" "}
                      {b.payment.overdue ? "· Vencido" : ""}
                    </span>
                  )}
                </div>
              )}
              {"confirmationStatus" in b &&
                b.confirmationStatus === "rejected" && (
                  <StatusBadge tone="danger">
                    {b.confirmationError ===
                    "confirmation_pdf_generation_failed"
                      ? "PDF con error"
                      : "Confirmación sin enviar"}
                  </StatusBadge>
                )}
              <ArrowRight size={18} />
            </Link>
          ))}
        </div>
      )}
      {!loading && !error && !rows.length && (
        <StatePanel
          state="empty"
          title="Sin reservas con estos filtros"
          description="Las reservas creadas desde una cotización o WordPress aparecerán aquí."
        />
      )}
      {!useDevelopmentFixtures && directory && (
        <div className="workspace-pagination">
          <Button
            variant="quiet"
            disabled={page <= 1 || loading}
            onClick={() => update("page", String(page - 1))}
          >
            Anterior
          </Button>
          <span>Página {page}</span>
          <Button
            variant="quiet"
            disabled={
              loading ||
              page * directory.pagination.perPage >= directory.pagination.total
            }
            onClick={() => update("page", String(page + 1))}
          >
            Siguiente
          </Button>
        </div>
      )}
    </section>
  );
}
